import React, { useEffect, useState } from "react";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Multiselect from "@cloudscape-design/components/multiselect";
import Toggle from "@cloudscape-design/components/toggle";
import Checkbox from "@cloudscape-design/components/checkbox";
import Alert from "@cloudscape-design/components/alert";
import { useTranslation } from "react-i18next";
import { api } from "../api";

export default function EditLegacyWebhookModal({ visible, webhook, onDismiss, onSaved }) {
  const { t } = useTranslation();

  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);

  const [regions, setRegions] = useState([]);
  const [services, setServices] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [includeGlobal, setIncludeGlobal] = useState(false);
  const [filterGlobalByAvailability, setFilterGlobalByAvailability] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load dropdown data once
  useEffect(() => {
    if (!visible) return;
    setError("");
    setLoading(true);
    (async () => {
      try {
        const [regionsData, servicesData] = await Promise.all([api.getRegions(), api.getServices()]);
        setAvailableRegions(regionsData.regions || []);
        setAvailableServices(servicesData.services || []);
      } catch (e) {
        setError(e.message || "Failed to load dropdown data");
      } finally {
        setLoading(false);
      }
    })();
  }, [visible]);

  // Populate from the webhook being edited
  useEffect(() => {
    if (!visible || !webhook) return;
    setRegions((webhook.regions || []).map((r) => ({ label: r, value: r })));
    setServices((webhook.services || []).map((s) => ({ label: s, value: s })));
    setKeywords((webhook.keywords || []).join(", "));
    setIncludeGlobal(Boolean(webhook.include_global));
    setFilterGlobalByAvailability(Boolean(webhook.filter_global_by_availability));
    setEnabled(Boolean(webhook.enabled));
  }, [visible, webhook]);

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const body = {
        enabled,
        filters: {
          regions: regions.map((r) => r.value),
          services: services.map((s) => s.value),
          keywords: keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k),
          include_global: includeGlobal,
          filter_global_by_availability: filterGlobalByAvailability,
        },
      };
      await api.updateLegacyWebhook(webhook.webhook_id, body);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={webhook ? `${t("admin.legacy.edit-title")}: ${webhook.webhook_id}` : t("admin.legacy.edit-title")}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={saving}>
              {t("admin.action.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={loading}>
              {t("admin.action.save")}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError("")}>
              {error}
            </Alert>
          )}

          <FormField label={t("admin.legacy.field.channel-name")}>
            <Input value={webhook?.channel_name || ""} readOnly disabled />
          </FormField>

          <FormField label={t("admin.legacy.field.enabled")}>
            <Toggle checked={enabled} onChange={({ detail }) => setEnabled(detail.checked)}>
              {enabled ? t("admin.legacy.status.enabled") : t("admin.legacy.status.disabled")}
            </Toggle>
          </FormField>

          <FormField label={t("form.field.regions")} description={t("form.field.regions-description")}>
            <Multiselect
              placeholder={loading ? t("admin.legacy.loading") : t("form.field.regions-placeholder-active")}
              selectedOptions={regions}
              onChange={({ detail }) => setRegions(detail.selectedOptions)}
              options={availableRegions.map((r) => ({ label: r.label || r.value, value: r.value }))}
              filteringType="auto"
              tokenLimit={20}
            />
          </FormField>

          <FormField label={t("form.field.services")} description={t("form.field.services-description")}>
            <Multiselect
              placeholder={loading ? t("admin.legacy.loading") : t("form.field.services-placeholder-active")}
              selectedOptions={services}
              onChange={({ detail }) => setServices(detail.selectedOptions)}
              options={availableServices.map((s) => ({ label: s.name || s, value: s.name || s }))}
              filteringType="auto"
              tokenLimit={20}
            />
          </FormField>

          <FormField label={t("form.field.keywords")} description={t("form.field.keywords-description")}>
            <Input value={keywords} onChange={({ detail }) => setKeywords(detail.value)} placeholder={t("form.field.keywords-placeholder")} />
          </FormField>

          <FormField label={t("form.field.include-global-announcements")}>
            <Checkbox checked={includeGlobal} onChange={({ detail }) => setIncludeGlobal(detail.checked)}>
              {t("form.field.include-global-announcements")}
            </Checkbox>
          </FormField>

          {includeGlobal && (
            <FormField label={t("form.field.filter-global-by-availability")}>
              <Checkbox checked={filterGlobalByAvailability} onChange={({ detail }) => setFilterGlobalByAvailability(detail.checked)}>
                {t("form.field.filter-global-by-availability")}
              </Checkbox>
            </FormField>
          )}
        </SpaceBetween>
      </Form>
    </Modal>
  );
}
